import { ClientMethodsTypes, client } from '@/services/v2/client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ProfileQueryKeys } from 'containers/profile/types/query-keys'
import { useUser } from 'containers/user/hooks/useUser'
import { UserQueryKeys } from 'containers/user/types/user'
import { useMemo } from 'react'
import {
  OAuthQueryKeys,
  OAuthStatusResponse,
  OauthLinkResponse,
  OauthRequestSocialType,
} from '../types/oAuth'

const BASE_URL = '/api/v1'

export const useOauthStatus = (profileId?: string) => {
  const { user } = useUser()

  const id = profileId || user?.id

  const { data, ...rest } = useQuery({
    queryFn: () =>
      client<OAuthStatusResponse>(
        `${BASE_URL}/oauth-general/oauth-connection-status?id=${id}`
      ),
    queryKey: [OAuthQueryKeys.GENERAL_STATUS, id],
  })

  const noSocialConnected = useMemo(() => {
    return (
      !data?.instagram &&
      !data?.tiktok &&
      !data?.youtube &&
      !data?.['instagram-basic']
    )
  }, [data])

  return {
    data,
    ...rest,
    noSocialConnected,
  }
}

export const useOauthConnectionLink = (social: OauthRequestSocialType) => {
  const { user } = useUser()

  return useQuery({
    queryFn: () => client<OauthLinkResponse>(`${BASE_URL}/${social}`),
    queryKey: [OAuthQueryKeys.CONNECTION_LINK, user?.id, social],
    enabled: !!user?.id && !!social,
  })
}

export const useOauthDisconnect = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (social: OauthRequestSocialType) => {
      return client(`${BASE_URL}/${social}/revoke-connection`, {
        method: ClientMethodsTypes.POST,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([OAuthQueryKeys.GENERAL_STATUS])
      await queryClient.invalidateQueries([ProfileQueryKeys.ANALYTICS])
      await queryClient.invalidateQueries([UserQueryKeys.CONNECTIONS])
    },
  })
}
